import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Trash2, FileText, Image as ImageIcon, Download, Loader2, Paperclip } from "lucide-react";
import { COLORBOND_COLOURS } from "@/lib/fencing";

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const QuoteAttachments = ({ quoteId }) => {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const { data: files = [] } = useQuery({
    queryKey: ["quote-files", quoteId],
    queryFn: async () => (await api.get(`/quotes/${quoteId}/attachments`)).data,
    enabled: !!quoteId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["quote-files", quoteId] });

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    setUploading(true);
    for (const f of picked) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} is over 15MB`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/quotes/${quoteId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${f.name} uploaded`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    invalidate();
  };

  const patch = useMutation({
    mutationFn: async ({ fid, data }) => (await api.patch(`/quotes/${quoteId}/attachments/${fid}`, data)).data,
    onSuccess: invalidate,
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });
  const del = useMutation({
    mutationFn: async (fid) => (await api.delete(`/quotes/${quoteId}/attachments/${fid}`)).data,
    onSuccess: () => { invalidate(); toast.success("Attachment removed"); },
  });

  const download = async (f) => {
    try {
      const res = await api.get(`/quotes/${quoteId}/attachments/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: f.content_type }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open file");
    }
  };

  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow p-4 sm:p-5" data-testid="quote-attachments-card">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="stamp-label text-cyan-600 flex items-center gap-1.5"><Paperclip size={13} /> Attachments — Photos &amp; Files</div>
          <p className="text-xs text-slate-500 mt-0.5">PDFs, brochures &amp; design images — shown on the client portal and attached to the quote email. Tag designs with colour &amp; height.</p>
        </div>
        <Button data-testid="attachment-upload-btn" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="rounded-sm h-9 border-cyan-300 text-cyan-700 hover:bg-cyan-50">
          {uploading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Upload size={14} className="mr-1.5" />}
          Upload files
        </Button>
        <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={onPick} data-testid="attachment-upload-input" />
      </div>

      {files.length === 0 && (
        <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-sm text-sm text-slate-400" data-testid="attachments-empty">
          No attachments yet — upload brochures, spec sheets or design drawings.
        </div>
      )}

      <div className="space-y-2">
        {files.map((f, i) => (
          <div key={f.id} className="border border-slate-200 rounded-sm p-3" data-testid={`attachment-row-${i}`}>
            <div className="flex items-center gap-2 flex-wrap">
              {f.content_type === "application/pdf"
                ? <FileText size={16} className="text-rose-500 shrink-0" />
                : <ImageIcon size={16} className="text-cyan-600 shrink-0" />}
              <Input
                defaultValue={f.label || f.filename}
                onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== f.label) patch.mutate({ fid: f.id, data: { label: v } }); }}
                className="h-8 rounded-sm text-sm font-medium flex-1 min-w-[160px]"
                data-testid={`attachment-label-${i}`}
              />
              <span className="text-[10px] text-slate-400 font-mono shrink-0">{fmtSize(f.size || 0)}</span>
              <Button size="sm" variant="ghost" onClick={() => download(f)} className="h-8 w-8 p-0 text-slate-500 hover:text-cyan-600" title="Open / download" data-testid={`attachment-download-${i}`}>
                <Download size={14} />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => del.mutate(f.id)} className="h-8 w-8 p-0 text-slate-500 hover:text-rose-600" title="Remove" data-testid={`attachment-delete-${i}`}>
                <Trash2 size={14} />
              </Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              <Select value={f.kind || "brochure"} onValueChange={(v) => patch.mutate({ fid: f.id, data: { kind: v } })}>
                <SelectTrigger className="h-8 rounded-sm text-xs" data-testid={`attachment-kind-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="brochure">Brochure</SelectItem>
                  <SelectItem value="design">Design</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={f.design_colour || "none"}
                onValueChange={(v) => {
                  if (v === "none") patch.mutate({ fid: f.id, data: { design_colour: null, design_colour_hex: null } });
                  else {
                    const c = COLORBOND_COLOURS.find((x) => x.name === v);
                    patch.mutate({ fid: f.id, data: { design_colour: v, design_colour_hex: c?.hex || null } });
                  }
                }}
              >
                <SelectTrigger className="h-8 rounded-sm text-xs" data-testid={`attachment-colour-${i}`}>
                  <SelectValue placeholder="Colour" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No colour —</SelectItem>
                  {COLORBOND_COLOURS.map((c) => (
                    <SelectItem key={c.name} value={c.name}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-3 h-3 rounded-sm border border-slate-200" style={{ backgroundColor: c.hex }} />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative">
                <Input
                  type="number" min="0" step="50" placeholder="Height (mm)"
                  defaultValue={f.design_height_mm ?? ""}
                  onBlur={(e) => {
                    const v = e.target.value === "" ? null : parseInt(e.target.value, 10) || 0;
                    if (v !== (f.design_height_mm ?? null)) patch.mutate({ fid: f.id, data: { design_height_mm: v } });
                  }}
                  className="h-8 rounded-sm text-xs font-mono pr-9"
                  data-testid={`attachment-height-${i}`}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">mm</span>
              </div>
            </div>
            {(f.design_colour || f.design_height_mm) && (
              <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500">
                <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider">{f.kind}</Badge>
                {f.design_colour && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm border border-slate-300" style={{ backgroundColor: f.design_colour_hex }} />
                    {f.design_colour}
                  </span>
                )}
                {f.design_height_mm ? <span>· {f.design_height_mm} mm</span> : null}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};
